import { useTranslation } from 'react-i18next';
import { SidebarSection, SidebarSectionSubtitle } from '../styles';
import { Button, Tooltip } from '@salecycle/ui-component-library';
import { ITemplate } from 'entities/template/template';
import styled from 'styled-components';
import { useFeatureFlags } from 'core/app/AppContext';
import { useCheckTemplateUsage } from '../../../../hooks/useCheckTemplateUsage';
import { useIsSaleCycleUser } from '../../../../hooks/useIsSaleCycleUser';
import { isUnsubscribeTemplate } from '../../../../utils/templateUtils';
import { useArchiveTemplate } from '../../../../hooks/useArchiveTemplate';

const ArchiveTemplateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export interface ArchiveTemplateButtonProps {
  selectedTemplate: ITemplate;
  getQueryKeysToInvalidate: () => string;
}

export const ArchiveTemplateButton = ({ selectedTemplate, getQueryKeysToInvalidate }: ArchiveTemplateButtonProps) => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages', 'templates']);
  const { flagEnabled } = useFeatureFlags();
  const isArchivingEnabled = flagEnabled('ld-archiving', false);
  const isSaleCycleUser = useIsSaleCycleUser();
  const isUnsubscribe = isUnsubscribeTemplate(selectedTemplate);

  const { isInUse, isCheck404, isLoading: isCheckingUsage } = useCheckTemplateUsage(
    selectedTemplate,
    isArchivingEnabled,
  );

  const archiveTemplateMutation = useArchiveTemplate(selectedTemplate, getQueryKeysToInvalidate);

  const handleArchive = () => {
    archiveTemplateMutation.mutate(undefined);
  };

  const isDisabled =
    isInUse || isCheckingUsage || isCheck404 || archiveTemplateMutation.isLoading || !selectedTemplate.channel;

  let tooltipMessage: string | undefined;
  if (isInUse) {
    tooltipMessage = t('designs:designGallery.sidebar.archiveButtonDisabledTooltip');
  } else if (isCheck404) {
    tooltipMessage = t('designs:designGallery.sidebar.archiveButtonCheckErrorTooltip');
  } else if (!selectedTemplate.channel) {
    tooltipMessage = t('designs:designGallery.sidebar.archiveButtonNoChannelTooltip');
  }

  if (!isArchivingEnabled || isUnsubscribe || !isSaleCycleUser) {
    return null;
  }

  const button = (
    <Button variation="primary" onClick={handleArchive} width={236} disabled={isDisabled}>
      {t('designs:designGallery.sidebar.archiveButton')}
    </Button>
  );

  return (
    <SidebarSection data-testid="ArchiveTemplate">
      <SidebarSectionSubtitle data-testid="ArchiveTemplateSubTitle">
        {t('designs:designGallery.sidebar.archiveSubtitle')}
      </SidebarSectionSubtitle>
      <ArchiveTemplateButtonContainer>
        {tooltipMessage ? <Tooltip title={tooltipMessage}>{button}</Tooltip> : button}
      </ArchiveTemplateButtonContainer>
    </SidebarSection>
  );
};
